import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HeaderHelperService } from '../header-helper.service';
import { UserListModel } from '../../models/user/user-list.model';
import { UserAvatarModel } from '../../models/user/user-avatar.model';
import { UserSettingsModel } from '../../models/user/user-settings.model';
import { FilterModel } from '../../models/filters/filters.model';
import { SortModel } from '../../models/filters/sort.model';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(
    private http: HttpClient,
    private headerHelperService: HeaderHelperService
  ) {}

  userAvatar: string | null = null;

  getUserList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): Observable<any> {
    let url = `${
      environment.apiLocations.identity
    }/Users?PageRequest.PageIndex=${page - 1}&PageRequest.PageSize=${size}`;
    if (filters) {
      const resultFilter = filters.filter((x) => !!x.value);
      url += resultFilter.map((f) => `&${f.filterId}=${f.value}`).join('');
    }
    if (sort) {
      url += `&SortBy=${sort.column}&SortDescending=${
        sort.orderBy == 'descending'
      }`;
    }

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getUser(id: string): Observable<any> {
    return this.http.get<UserListModel[]>(
      `${environment.apiLocations.identity}/Users/${id}`,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  createUser(userData: any): Observable<UserListModel[]> {
    return this.http.post<UserListModel[]>(
      `${environment.apiLocations.identity}/Users`,
      userData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  editUser(id: string, userData: any): Observable<UserListModel[]> {
    return this.http.put<UserListModel[]>(
      `${environment.apiLocations.identity}/Users/${id}`,
      userData,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiLocations.identity}/Users/${id}`,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  expertList(): Observable<any> {
    return this.http.get<any>(
      `${environment.apiLocations.identity}/Users/GetExpertList`,
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  blockUser(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.apiLocations.identity}/Users/BlockUser/${id}`,
      {},
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  activateUser(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.apiLocations.identity}/Users/ActivateUser/${id}`,
      {},
      {
        headers: this.headerHelperService.getHeaders(),
      }
    );
  }

  async getUserBranchesByCustomerCode(): Promise<any> {
    let url = `${environment.apiLocations.identity}/Users/GetUserBranchesByCustomerCode`;
    const observable: Observable<any> = this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });

    const promiseData = firstValueFrom(observable);
    return await promiseData;
  }

  async getUserAvatar(): Promise<UserAvatarModel> {
    if (this.userAvatar) return { avatar: this.userAvatar.split(',')[1] };
    let url = `${environment.apiLocations.identity}/Users/GetUserAvatar`;
    const observable: Observable<UserAvatarModel> =
      this.http.get<UserAvatarModel>(url, {
        headers: this.headerHelperService.getHeaders(),
      });

    const promiseData = await firstValueFrom(observable);
    this.userAvatar = `${promiseData.avatar}`;
    return promiseData;
  }

  updateUserPrice(userData: UserSettingsModel): Observable<any> {
    let url = `${environment.apiLocations.identity}/Users/UpdateUserPrice`;

    return this.http.put<any>(url, userData, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  getUserNotificationSettings(email: string): Observable<any> {
    let url = `${environment.apiLocations.identity}/UserNotificationSettings/GetUserNotificationSettingsByEmail/${email}`;

    return this.http.get<any>(url, {
      headers: this.headerHelperService.getHeaders(),
    });
  }

  sendUserNotificationSettings(userData: any): Observable<any> {
    let url = `${environment.apiLocations.identity}/UserNotificationSettings`;

    return this.http.post<any>(url, userData, {
      headers: this.headerHelperService.getHeaders(),
    });
  }
}
